import { useCallback, useEffect, useState } from 'react'

/** Drafts older than this are treated as abandoned. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

/** Font, size and colour a customer picked for an engraving step. */
export interface StampStyle {
  font: string
  size: number
  color: string
}

export interface CustomizeDraft {
  step: number
  /** Keyed by step key, so it survives the configurator being reordered. */
  selections: Record<string, string>
  /** Engraving styling, likewise keyed by step. */
  stampStyles?: Record<string, StampStyle>
}

/**
 * Persists the build half of the configurator so closing the sheet —
 * or the tab — doesn't cost the customer their choices. They come back
 * to the same step with the same selections.
 *
 * Scoped per product: two configurable pieces don't share a draft.
 *
 * Only the build is stored. Delivery details and card fields never
 * touch localStorage.
 *
 * `ready` flips once the stored draft has been read. Callers must wait
 * for it before writing: on mount the read effect and the write effect
 * fire in the same commit, and an eager write would overwrite the very
 * draft we are about to restore.
 */
export function useCustomizeDraft(productId: number) {
  const key = `sss-customize-draft:${productId}`
  const [draft, setDraft] = useState<CustomizeDraft | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(key)
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<CustomizeDraft> & { savedAt?: number }
        if (parsed.savedAt && Date.now() - parsed.savedAt < MAX_AGE_MS) {
          setDraft({
            step: parsed.step ?? 0,
            selections: parsed.selections ?? {},
            stampStyles: parsed.stampStyles ?? {},
          })
        } else {
          window.localStorage.removeItem(key)
        }
      }
    } catch {
      // A corrupt draft is not worth blocking the funnel over.
    }
    setReady(true)
  }, [key])

  const save = useCallback(
    (next: CustomizeDraft) => {
      try {
        window.localStorage.setItem(key, JSON.stringify({ ...next, savedAt: Date.now() }))
      } catch {
        // Private mode / quota — the funnel still works, just not resumable.
      }
    },
    [key]
  )

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(key)
    } catch {
      // Ignore.
    }
  }, [key])

  return { draft, ready, save, clear }
}
